/**
 * Advanced Sensor Fusion Library for Compass Applications
 * Implements complementary filter with gravity compensation and bias correction
 * Based on industry best practices for IMU sensor fusion
 */

export interface SensorData {
    "accelerometer": [number, number, number]; // [x, y, z] in m/s² (including gravity)
    "gyroscope":     [number, number, number];     // [x, y, z] in degrees/second
    "magnetometer":  [number, number, number];  // [x, y, z] in μT
    "timestamp":     number;
}

export interface FusionState {
    "heading":       number;
    "pitch":         number;
    "roll":          number;
    "gyroscopeBias": [number, number, number];
    "lastUpdate":    number;
    "alpha":         number; // Complementary filter coefficient
}

export class SensorFusion {

    private state!: FusionState;

    private readonly gravityThreshold = 0.1; // Threshold for detecting gravity

    private readonly biasLearningRate = 0.001; // Rate for bias estimation

    constructor( initialHeading: number = 0, alpha: number = 0.98 ) {
        this.state = {
            "heading":       initialHeading,
            "pitch":         0,
            "roll":          0,
            "gyroscopeBias": [ 0, 0, 0 ],
            "lastUpdate":    Date.now(),
            alpha,
        };
    }

    /**
     * Update fusion state with new sensor data
     * @param data - Sensor data from DeviceMotionEvent and DeviceOrientationEvent
     * @returns Updated orientation state
     */
    update( data: SensorData ): FusionState {
        const deltaTime = ( data.timestamp - this.state.lastUpdate ) / 1000;

        if ( deltaTime <= 0 ) {
            return this.state;
        }

        // Update gyroscope bias estimation
        this.updateGyroscopeBias( data.gyroscope );

        // Apply bias correction to gyroscope data
        const correctedGyro = this.correctGyroscopeBias( data.gyroscope );

        // Calculate orientation from accelerometer (gravity vector)
        const accelOrientation = this.calculateOrientationFromAccelerometer( data.accelerometer );

        // Calculate heading from magnetometer with tilt compensation
        const magnetometerHeading = this.calculateTiltCompensatedHeading(
            data.accelerometer,
            data.magnetometer,
        );

        // Update gyroscope-based orientation
        this.updateGyroscopeOrientation( correctedGyro, deltaTime );

        // Apply complementary filter
        this.applyComplementaryFilter( magnetometerHeading, accelOrientation );

        this.state.lastUpdate = data.timestamp;

        return this.state;
    }

    /**
     * Update gyroscope bias estimation
     * @param gyroscope - Raw gyroscope data
     */
    private updateGyroscopeBias( gyroscope: [number, number, number] ): void {
    // Simple bias estimation - in practice, you'd want more sophisticated methods
        const [ x, y, z ] = gyroscope;

        // Only update bias when device is relatively still
        const magnitude = Math.sqrt( x ** 2 + y ** 2 + z ** 2 );
        if ( magnitude < this.gravityThreshold ) {
            this.state.gyroscopeBias[0] += x * this.biasLearningRate;
            this.state.gyroscopeBias[1] += y * this.biasLearningRate;
            this.state.gyroscopeBias[2] += z * this.biasLearningRate;
        }
    }

    /**
     * Apply bias correction to gyroscope data
     * @param gyroscope - Raw gyroscope data
     * @returns Corrected gyroscope data
     */
    private correctGyroscopeBias( gyroscope: [number, number, number] ): [number, number, number] {
        return [
            gyroscope[0] - this.state.gyroscopeBias[0],
            gyroscope[1] - this.state.gyroscopeBias[1],
            gyroscope[2] - this.state.gyroscopeBias[2],
        ];
    }

    /**
     * Calculate orientation from accelerometer data (gravity vector)
     * @param accelerometer - Accelerometer data including gravity
     * @returns Orientation angles in degrees
     */
    private calculateOrientationFromAccelerometer( accelerometer: [number, number, number] ): {
        "pitch": number;
        "roll":  number;
    } {
        const [ x, y, z ] = accelerometer;

        // Normalize accelerometer data
        const magnitude = Math.sqrt( x ** 2 + y ** 2 + z ** 2 );
        if ( magnitude === 0 ) {
            return {
                "pitch": 0,
                "roll":  0,
            };
        }

        const normX = x / magnitude;
        const normY = y / magnitude;
        const normZ = z / magnitude;

        // Calculate pitch and roll from gravity vector
        const pitch = Math.atan2( -normX, Math.sqrt( normY ** 2 + normZ ** 2 ) ) * 180 / Math.PI;
        const roll = Math.atan2( normY, normZ ) * 180 / Math.PI;

        return {
            pitch,
            roll,
        };
    }

    /**
     * Calculate tilt-compensated heading from magnetometer data
     * @param accelerometer - Accelerometer data for tilt compensation
     * @param magnetometer - Magnetometer data
     * @returns Compensated heading in degrees [0, 360)
     */
    private calculateTiltCompensatedHeading(
        accelerometer: [number, number, number],
        magnetometer: [number, number, number],
    ): number {
        const [ accelX, accelY, accelZ ] = accelerometer;
        const [ magX, magY, magZ ] = magnetometer;

        // Normalize accelerometer data
        const accelMagnitude = Math.sqrt( accelX ** 2 + accelY ** 2 + accelZ ** 2 );
        if ( accelMagnitude === 0 ) {
            return 0;
        }

        const normAccelX = accelX / accelMagnitude;
        const normAccelY = accelY / accelMagnitude;
        const normAccelZ = accelZ / accelMagnitude;

        // Calculate roll and pitch from accelerometer
        const roll = Math.atan2( normAccelY, normAccelZ );
        const pitch = Math.atan2( -normAccelX, Math.sqrt( normAccelY ** 2 + normAccelZ ** 2 ) );

        // Apply tilt compensation to magnetometer data
        const cosRoll = Math.cos( roll );
        const sinRoll = Math.sin( roll );
        const cosPitch = Math.cos( pitch );
        const sinPitch = Math.sin( pitch );

        const compensatedMagX = magX * cosPitch + magY * sinRoll * sinPitch + magZ * cosRoll * sinPitch;
        const compensatedMagY = magY * cosRoll - magZ * sinRoll;

        // Calculate heading from compensated magnetometer data
        const heading = Math.atan2( -compensatedMagY, compensatedMagX ) * 180 / Math.PI;

        // Normalize to [0, 360)
        return ( heading + 360 ) % 360;
    }

    /**
     * Update gyroscope-based orientation
     * @param correctedGyro - Bias-corrected gyroscope data
     * @param deltaTime - Time delta in seconds
     */
    private updateGyroscopeOrientation( correctedGyro: [number, number, number], deltaTime: number ): void {
        const [ gyroX, gyroY, gyroZ ] = correctedGyro;

        // Update orientation using gyroscope data
        this.state.pitch += gyroY * deltaTime;
        this.state.roll += gyroX * deltaTime;
        this.state.heading += gyroZ * deltaTime;

        // Normalize angles
        this.state.pitch = this.normalizeAngle( this.state.pitch );
        this.state.roll = this.normalizeAngle( this.state.roll );
        this.state.heading = this.normalizeAngle( this.state.heading );
    }

    /**
     * Apply complementary filter to fuse accelerometer and gyroscope data
     * @param magnetometerHeading - Heading from magnetometer
     * @param accelOrientation - Orientation from accelerometer
     */
    private applyComplementaryFilter(
        magnetometerHeading: number,
        accelOrientation: {
            "pitch": number;
            "roll":  number;
        },
    ): void {
        const alpha = this.state.alpha;

        // Fuse pitch and roll
        this.state.pitch = alpha * accelOrientation.pitch + ( 1 - alpha ) * this.state.pitch;
        this.state.roll = alpha * accelOrientation.roll + ( 1 - alpha ) * this.state.roll;

        // Fuse heading
        this.state.heading = alpha * magnetometerHeading + ( 1 - alpha ) * this.state.heading;
    }

    /**
     * Normalize angle to [-180, 180] range
     * @param angle - Angle in degrees
     * @returns Normalized angle
     */
    private normalizeAngle( angle: number ): number {
        while ( angle > 180 ) {
            angle -= 360;
        }
        while ( angle < -180 ) {
            angle += 360;
        }

        return angle;
    }

    /**
     * Get current fusion state
     * @returns Current orientation state
     */
    getState(): FusionState {
        return { ...this.state };
    }

    /**
     * Set complementary filter coefficient
     * @param alpha - Filter coefficient (0-1, higher = more accelerometer/magnetometer)
     */
    setAlpha( alpha: number ): void {
        this.state.alpha = Math.max( 0, Math.min( 1, alpha ) );
    }

    /**
     * Reset fusion state
     * @param initialHeading - Initial heading value
     */
    reset( initialHeading: number = 0 ): void {
        this.state = {
            "heading":       initialHeading,
            "pitch":         0,
            "roll":          0,
            "gyroscopeBias": [ 0, 0, 0 ],
            "lastUpdate":    Date.now(),
            "alpha":         this.state.alpha,
        };
    }

}
